import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import { Email as EmailIcon, Link as LinkIcon } from "@mui/icons-material";
import moment from "moment";

/**
 * Displays share activity events (email/link sends, narrator updates) for a quicktale.
 *
 * Props:
 * - events: array of shareActivity docs from useShareActivityEvents hook
 * - loading: boolean
 * - templates: array of email templates (used to resolve templateId -> name)
 */
const ShareActivityTable = ({ events = [], loading = false, templates = [] }) => {
  // Build a map of templateId -> template name for display
  const templateNameMap = React.useMemo(() => {
    const map = {};
    (templates || []).forEach((t) => {
      if (t?.id) map[t.id] = t.name || t.templateName || "Unnamed Template";
    });
    return map;
  }, [templates]);

  const formatTimestamp = (ts) => {
    if (!ts) return "—";
    // Firestore timestamps have .toDate(), plain Date objects don't
    try {
      const date = ts.toDate ? ts.toDate() : new Date(ts);
      return moment(date).format("MM/DD/YYYY h:mm A");
    } catch {
      return "—";
    }
  };

  const getEventChip = (eventType) => {
    if (eventType === "email") {
      return (
        <Chip
          icon={<EmailIcon sx={{ fontSize: 16 }} />}
          label="Email"
          size="small"
          color="primary"
          variant="outlined"
          sx={{ fontFamily: "Avenir Book, sans-serif" }}
        />
      );
    }
    return (
      <Chip
        icon={<LinkIcon sx={{ fontSize: 16 }} />}
        label="Link"
        size="small"
        color="secondary"
        variant="outlined"
        sx={{ fontFamily: "Avenir Book, sans-serif" }}
      />
    );
  };

  const toTitleCase = (str) =>
  str.replace(/\w\S*/g, word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());


  const getEmailStatusChip = (emailStatus, eventType) => {
    if (eventType !== "email" || !emailStatus) return "—";
    const statusConfig = {
      sent: { label: "Sent", color: "default" },
      processed: { label: "Processed", color: "default" },
      deferred: { label: "Deferred", color: "warning" },
      delivered: { label: "Delivered", color: "success" },
      opened: { label: "Opened", color: "success" },
      bounced: { label: "Bounced", color: "error" },
      dropped: { label: "Dropped", color: "error" },
      spam_report: { label: "Spam", color: "error" },
    };
    const config = statusConfig[emailStatus] || { label: emailStatus, color: "default" };
    return (
      <Chip
        label={config.label}
        size="small"
        color={config.color}
        sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 12 }}
      />
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
  <TableContainer component={Paper} sx={{ boxShadow: "none", border: "1px solid #e0e0e0" }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
          <TableCell sx={headerCellSx}>Type</TableCell>
          <TableCell sx={headerCellSx}>Date Sent</TableCell>
          <TableCell sx={headerCellSx}>Template</TableCell>
          <TableCell sx={headerCellSx}>Narrator Name</TableCell>
          <TableCell sx={headerCellSx}>Narrator Email</TableCell>
          <TableCell sx={headerCellSx}>Email Status</TableCell>
          <TableCell sx={headerCellSx}>Date Updated</TableCell>
        </TableRow>
      </TableHead>
      {events && events.length > 0 && (
        <TableBody>
          {events.map((event) => (
            <TableRow key={event.id} hover>
              <TableCell sx={bodyCellSx}>{toTitleCase(event.eventType)}</TableCell>
              <TableCell sx={bodyCellSx}>{formatTimestamp(event.createdAt)}</TableCell>
              <TableCell sx={bodyCellSx}>
                {event.templateId
                  ? templateNameMap[event.templateId] || event.templateId
                  : "—"}
              </TableCell>
              <TableCell sx={bodyCellSx}>{event.narratorName || "—"}</TableCell>
              <TableCell sx={bodyCellSx}>{event.narratorEmail || "—"}</TableCell>
              <TableCell sx={bodyCellSx}>{getEmailStatusChip(event.emailStatus, event.eventType)}</TableCell>
              <TableCell sx={bodyCellSx}>
                {event.updatedAt ? (
                  <span style={{ color: "#2e7d32", fontWeight: 500 }}>
                    {formatTimestamp(event.updatedAt)}
                  </span>
                ) : (
                  "—"
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      )}
    </Table>
    {(!events || events.length === 0) && (
      <Typography
        sx={{
          fontFamily: "Avenir Book, sans-serif",
          color: "#999",
          fontSize: 14,
          py: 1,
          px: 2,
        }}
      >
        No narrator activity yet.
      </Typography>
    )}
  </TableContainer>
);
};

const headerCellSx = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 13,
  color: "#333",
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  fontFamily: "Avenir Book, sans-serif",
  fontSize: 13,
  color: "#555",
};

export default ShareActivityTable;
